import { KanbanizeBoard, KanbanizeError, RepoLink, Repository } from "../types/kanbanizeTypes";

/**
 * Checks if an object is a Businessmap error.
 * @param check {any}
 * @returns boolean
 */
function isKanbanizeError(check: any): check is KanbanizeError {
	if (check.error && check.error.code) {
		return true;
	}
	return false;
}

function isError(check: any): check is { error: any } {
	if (check.error) {
		return true;
	}
	return false;
}

function isActualError(check: any): check is Error {
	if (check && check.stack && check.message) return true;
	return false;
}

async function doGet<T = any>(url: string, authToken?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;
	const response = await fetch(url, fetchCallOptions);
	const jsonData: T = await response.json();
	if (isActualError(jsonData)) return Promise.reject(jsonData);
	return jsonData;
}

async function doPost<T = any>(url: string, payload: any, authToken?: string, basic?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "POST",
		headers: {
			"Content-Type": basic ? "application/x-www-form-urlencoded" : "application/json",
		},
		body: basic ? payload : JSON.stringify(payload),
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;
	//Basic is just used for oauth calls
	if (basic) fetchCallOptions.headers.authorization = `Basic ${btoa(basic)}`;

	const response = await fetch(url, fetchCallOptions);
	const jsonData: T = await response.json();
	if (isActualError(jsonData)) return Promise.reject(jsonData);
	return jsonData;
}

async function doPut<T = any>(url: string, payload: any, authToken?: string, basic?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "PUT",
		headers: {
			"Content-Type": basic ? "application/x-www-form-urlencoded" : "application/json",
		},
		body: basic ? payload : JSON.stringify(payload),
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;
	//Basic is just used for oauth calls
	if (basic) fetchCallOptions.headers.authorization = `Basic ${btoa(basic)}`;

	const response = await fetch(url, fetchCallOptions);
	const jsonData: T = await response.json();
	if (isActualError(jsonData)) return Promise.reject(jsonData);
	return jsonData;
}

async function doDelete<T = any>(url: string, authToken?: string): Promise<T> {
	const fetchCallOptions: any = {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
		},
	};
	if (authToken) fetchCallOptions.headers.authorization = `Bearer ${authToken}`;

	const response = await fetch(url, fetchCallOptions);
	const jsonData: T = await response.json();
	if (isActualError(jsonData)) return Promise.reject(jsonData);
	return jsonData;
}
async function getUserNameByCode(integrationDomain: string, code: string, state: string|null) {
	const integration = process.env.REACT_APP_INTEGRATION_SERVICE as string;
	const payload: { [key: string]: any } = {
		code: code,
		state: state
	};
	if (integration === "GitHub") {
		payload.client_id = process.env.REACT_APP_GH_CLIENT_ID;
	}
	if (integration === "BitBucket") payload.grant_type = "authorization_code";
	let tokenData: any;
	if (integration === "GitHub") tokenData = await doPost(`${integrationDomain}/api/github/access_token`, payload);
	if (integration === "BitBucket")
		tokenData = await doPost(
			`https://bitbucket.org/site/oauth2/access_token`,
			jsonToForm(payload),
			undefined,
			`${process.env.REACT_APP_BB_OAUTH_CLIENT_ID}:${process.env.REACT_APP_BB_OAUTH_CLIENT_SECRET}`,
		);
	let token: string = "";
	if (integration === "GitHub") return tokenData.username;
	if (integration === "BitBucket") token = tokenData.access_token;

	let userUrl = "";
	if (integration === "BitBucket") userUrl = "https://api.bitbucket.org/2.0/user";

	const userData = await doGet(userUrl, token);
	let userName = "";
	if (integration === "BitBucket") userName = userData.uuid;
	return userName;
}

async function populateRepos(integrationUrl: string, authToken: string) {
	let url = "";
	let repoNames: Repository[] = [];
	const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
	if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
	if (integration !== "GitHub") {
		url = `${integrationUrl}/api/${integration.toLowerCase()}/repos`;
		repoNames = await doGet(url);
	} else {
		url = `${integrationUrl}/api/repos`;
		repoNames = await doGet(url, authToken);
	}

	return repoNames;
}

const repository2SelectFormat = (repos: Repository[]) => {
	const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
	if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
	return repos.map(item => {
		if (integration === "GitLab") {
			if (!item.repo_id) throw Error("Missing id for repos");
			return { value: item.repo_id.toString(), label: item.repo_name as string };
		}
		if (integration === "GitHub") {
			return { value: item.repo_name as string, label: item.repo_name as string };
		}
		if (integration === "BitBucket") {
			return { value: item.full_name as string, label: item.full_name as string };
		}
		throw new Error(`Unrecogniced integration: ${integration}`);
	});
};

const boards2SelectFormat = (boards: KanbanizeBoard[]) => {
	return boards.map(item => {
		return { value: item.board_id.toString(), label: item.name };
	});
};

const jsonToForm = (json: { [key: string]: any }): string => {
	return Object.keys(json)
		.map(item => `${encodeURIComponent(item)}=${encodeURIComponent(json[item])}`)
		.join("&");
};

const refreshUserRepos = async (integration_domain: string) => {
	let url: string;
	if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
		url = `${integration_domain}/api/db/refresh_user_repos`;
	} else {
		url = `${integration_domain}/api/refresh_user_repos`;
	}
	return await doPost<{ savedConfigs: RepoLink[]; allRepos: Repository[] }>(url, {});
};

const refreshBoards = async (integration_domain: string, user_token?: string) => {
	console.log("utils/refreshBoards: init");
	let url = "";
	if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
		url = `${integration_domain}/api/kanbanize/boards`;
	} else {
		url = `${integration_domain}/api/availableBoards`;
	}
	const boards: KanbanizeBoard[] = await doGet(url, user_token);
	return boards;
};

const refreshRepolinksAndRepos = async (integration_domain: string, kanbanize_domain: string, user_token?: string) => {
	const payload = {
		domain: kanbanize_domain,
	};
	let url = "";
	let results: { savedConfigs: RepoLink[]; allRepos: Repository[] };
	if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
		url = `${integration_domain}/api/db/repoLink`;
		results = await doGet(url);
	} else {
		url = `${integration_domain}/api/listConfigs`;
		results = await doPost(url, payload, user_token);
	}
	return results;
};

export {
	isKanbanizeError,
	isError,
	doGet,
	doPost,
	doPut,
	doDelete,
	populateRepos,
	getUserNameByCode,
	isActualError,
	repository2SelectFormat,
	boards2SelectFormat,
	refreshUserRepos,
	refreshBoards,
	refreshRepolinksAndRepos,
};
